import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { US_STATES } from '../config/usStates';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CloudUpload, PhotoCamera } from '@mui/icons-material';
import AddressAutocomplete from '../components/AddressAutocomplete';
import SalesTaxRatesEditor from '../components/SalesTaxRatesEditor';
import { getStatesFromZipCodes } from '../utils/zipToState';
import { mergeSalesTaxRatesForStates, ProviderSalesTaxRate } from '../utils/salesTax';

const RegisterPage: React.FC = () => {
  const location = useLocation();
  const preSelectedUserType = (location.state as any)?.userType || 'customer';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    userType: preSelectedUserType as 'customer' | 'provider',
    businessName: '',
    serviceAreas: '',
    services: {
      lawnMowingTrimming: false,
      weedControlFertilizing: false,
      snowRemoval: false,
    },
    street: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessLogo, setBusinessLogo] = useState<string>('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [insuranceProof, setInsuranceProof] = useState<string>('');
  const [insuranceUploading, setInsuranceUploading] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState({
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: '',
  });
  const [w9Document, setW9Document] = useState<string>('');
  const [w9Uploading, setW9Uploading] = useState(false);
  const [w9Details, setW9Details] = useState({
    taxId: '',
    businessName: '',
    businessType: 'individual' as 'individual' | 'llc' | 'corporation' | 'partnership' | 'other',
  });
  const [pesticideLicenseDocument, setPesticideLicenseDocument] = useState<string>('');
  const [pesticideLicenseUploading, setPesticideLicenseUploading] = useState(false);
  const [pesticideLicenseDetails, setPesticideLicenseDetails] = useState({
    pesticideLicenseNumber: '',
    pesticideLicenseExpiryDate: '',
  });
  const [agreedToProviderTerms, setAgreedToProviderTerms] = useState(false);
  const [agreedToCustomerTerms, setAgreedToCustomerTerms] = useState(false);
  const [salesTaxRates, setSalesTaxRates] = useState<ProviderSalesTaxRate[]>([]);
  const [salesTaxCoverageAttested, setSalesTaxCoverageAttested] = useState(false);
  const [agreedToStripeTerms, setAgreedToStripeTerms] = useState(false);
  const [agreedToSMSMessaging, setAgreedToSMSMessaging] = useState(false);
  const [providerAgreement, setProviderAgreement] = useState('');
  const [customerAgreement, setCustomerAgreement] = useState('');
  const [agreementDialog, setAgreementDialog] = useState({ open: false, type: 'provider' as 'provider' | 'customer' });

  const { register, getUserDashboardRoute } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (formData.userType !== 'provider') {
      setSalesTaxRates([]);
      setSalesTaxCoverageAttested(false);
      return;
    }
    const zips = formData.serviceAreas
      .split(/[\s,\n]+/)
      .map((z) => z.trim())
      .filter((z) => /^\d{5}/.test(z));
    const states = getStatesFromZipCodes(zips);
    setSalesTaxRates((prev) => {
      const next = mergeSalesTaxRatesForStates(prev, states);
      if (next.length !== prev.length || next.some((r, i) => r.state !== prev[i]?.state)) {
        setSalesTaxCoverageAttested(false);
      }
      return next;
    });
  }, [formData.serviceAreas, formData.userType]);

  useEffect(() => {
    if (formData.userType === 'provider') {
      loadProviderAgreement();
    } else if (formData.userType === 'customer') {
      loadCustomerAgreement();
    }
  }, [formData.userType]);

  const loadProviderAgreement = async () => {
    try {
      const response = await apiService.get('/legal/provider-agreement');
      if (response.success && response.data) {
        setProviderAgreement((response.data as any).agreement);
      } else {
        setProviderAgreement('Provider agreement could not be loaded. Please contact support.');
      }
    } catch (err) {
      console.error('Failed to load provider agreement:', err);
      setProviderAgreement('Provider agreement could not be loaded. Please contact support.');
    }
  };

  const loadCustomerAgreement = async () => {
    try {
      const response = await apiService.get('/legal/customer-agreement');
      if (response.success && response.data) {
        setCustomerAgreement((response.data as any).agreement);
      } else {
        setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
      }
    } catch (err) {
      console.error('Failed to load customer agreement:', err);
      setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
    }
  };

  const handleViewAgreement = (type: 'provider' | 'customer') => {
    setAgreementDialog({ open: true, type });
  };

  const handleCloseAgreement = () => {
    setAgreementDialog({ ...agreementDialog, open: false });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const inputValue = e.target.value;
    
    setFormData(prev => {
      let value = inputValue;
      
      // Format phone number to (xxx) xxx-xxxx
      if (name === 'phone') {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 10) {
          if (cleaned.length === 0) {
            value = '';
          } else if (cleaned.length <= 3) {
            value = `(${cleaned}`;
          } else if (cleaned.length <= 6) {
            value = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
          } else {
            value = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
          }
        } else {
          value = prev.phone; // Don't allow input beyond 10 digits
        }
      }
      
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSelectChange = (e: any) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddressSelect = (fullAddress: string, addressComponents: any) => {
    setFormData(prev => ({
      ...prev,
      street: addressComponents.street || prev.street,
      city: addressComponents.city || prev.city,
      state: addressComponents.state || prev.state,
      zipCode: addressComponents.zipCode || prev.zipCode,
    }));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be smaller than 5MB');
      return;
    }

    try {
      setLogoUploading(true);
      setError('');

      // Convert image to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setBusinessLogo(base64String);
        setLogoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
      setLogoUploading(false);
    }
  };

  const handleInsuranceUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (allow images and PDFs)
    const validTypes = ['image/', 'application/pdf'];
    if (!validTypes.some(type => file.type.startsWith(type))) {
      setError('Please select a valid image or PDF file for insurance proof');
      return;
    }

    // Validate file size (max 10MB for insurance documents)
    if (file.size > 10 * 1024 * 1024) {
      setError('Insurance document must be smaller than 10MB');
      return;
    }

    try {
      setInsuranceUploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setInsuranceProof(base64String);
        setInsuranceUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload insurance proof');
      setInsuranceUploading(false);
    }
  };

  const handleInsuranceDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInsuranceDetails({
      ...insuranceDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleW9Upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF only for W-9)
    if (file.type !== 'application/pdf') {
      setError('W-9 form must be a PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('W-9 document must be smaller than 10MB');
      return;
    }

    try {
      setW9Uploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setW9Document(base64String);
        setW9Uploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload W-9 form');
      setW9Uploading(false);
    }
  };

  const handleW9DetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    setW9Details({
      ...w9Details,
      [target.name]: target.value,
    });
  };

  const handlePesticideLicenseUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (allow images and PDFs)
    const validTypes = ['image/', 'application/pdf'];
    if (!validTypes.some(type => file.type.startsWith(type))) {
      setError('Please select a valid image or PDF file for pesticide license');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Pesticide license document must be smaller than 10MB');
      return;
    }

    try {
      setPesticideLicenseUploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setPesticideLicenseDocument(base64String);
        setPesticideLicenseUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload pesticide license');
      setPesticideLicenseUploading(false);
    }
  };

  const handlePesticideLicenseDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPesticideLicenseDetails({
      ...pesticideLicenseDetails,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (formData.userType === 'provider' && !agreedToProviderTerms) {
      setError('You must agree to the Provider Service Agreement to register as a provider');
      return;
    }

    if (formData.userType === 'provider' && salesTaxRates.length > 0 && !salesTaxCoverageAttested) {
      setError('Please confirm that all states for which you are responsible for sales tax remittance are listed');
      return;
    }

    if (formData.userType === 'provider' && !w9Document) {
      setError('W-9 form is required. IRS requires a W-9 before we can pay you.');
      return;
    }

    if (formData.userType === 'provider' && !w9Details.taxId) {
      setError('Tax ID (EIN or SSN) is required on W-9 form');
      return;
    }

    // Insurance and pesticide license are optional at signup (tools-only access).
    // If a partial pesticide upload is started, require the license number.
    if (
      formData.userType === 'provider' &&
      pesticideLicenseDocument &&
      !pesticideLicenseDetails.pesticideLicenseNumber
    ) {
      setError('Please enter your pesticide license number, or remove the license upload');
      return;
    }

    if (formData.userType === 'customer' && !agreedToCustomerTerms) {
      setError('You must agree to the Customer Service Agreement to register');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: formData.userType,
        phone: formData.phone,
        agreementAccepted: true,
        agreedToTerms: formData.userType === 'customer' ? agreedToCustomerTerms : agreedToProviderTerms,
        agreedToStripeTerms: agreedToStripeTerms,
        agreedToSMSMessaging: agreedToSMSMessaging,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
        ...(formData.userType === 'provider' && {
          businessName: formData.businessName,
          serviceAreas: formData.serviceAreas
            .split(/[\s,\n]+/) // Split by comma, space, or newline
            .map(area => area.trim())
            .filter(area => area.length === 5 && /^\d{5}$/.test(area)), // Only valid 5-digit zip codes
          salesTaxRates,
          salesTaxCoverageAttested,
          services: Object.entries(formData.services)
            .filter(([_, selected]) => selected)
            .map(([service]) => {
              const serviceMap: { [key: string]: string } = {
                'lawnMowingTrimming': 'mowing',
                'weedControlFertilizing': 'fertilizing',
                'snowRemoval': 'snow_removal',
              };
              return serviceMap[service] || service;
            }),
          ...(businessLogo && { businessLogo }),
          ...(insuranceProof && { 
            insuranceProof,
            ...insuranceDetails,
          }),
          ...(w9Document && {
            w9Document,
            w9TaxId: w9Details.taxId,
            w9BusinessName: w9Details.businessName || formData.businessName,
            w9BusinessType: w9Details.businessType,
            w9OnFile: true,
            w9SubmittedDate: new Date().toISOString(),
          }),
          ...(pesticideLicenseDocument && {
            pesticideLicenseDocument,
            pesticideLicenseNumber: pesticideLicenseDetails.pesticideLicenseNumber,
            pesticideLicenseExpiryDate: pesticideLicenseDetails.pesticideLicenseExpiryDate || undefined,
            pesticideLicenseOnFile: true,
            pesticideLicenseSubmittedDate: new Date().toISOString(),
          }),
        }),
      };

      const user = await register(userData);
      // Marketplace customers: go straight to the new-service request form.
      // Provider-onboarded customers use InvitationSignupPage → /customer (offers to accept/decline).
      if ((user.userType || formData.userType).toLowerCase() === 'customer') {
        navigate('/request-service?new=1', { replace: true });
      } else {
        const dashboardRoute = getUserDashboardRoute(user);
        navigate(dashboardRoute, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            Join LawnHub
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your free account to get started
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Account Type</InputLabel>
                <Select
                  name="userType"
                  value={formData.userType}
                  label="Account Type"
                  onChange={handleSelectChange}
                  disabled={loading}
                >
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="provider">Service Provider</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="firstName"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                helperText="Must be at least 6 characters"
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            {formData.userType === 'provider' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="businessName"
                    label="Business Name"
                    name="businessName"
                    placeholder="e.g., GreenLawn"
                    value={formData.businessName}
                    onChange={handleChange}
                    disabled={loading}
                    helperText="This is the name customers will see on your bids and in their services."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    id="serviceAreas"
                    label="Service Areas - Zip Codes You Service"
                    name="serviceAreas"
                    placeholder="Enter zip codes separated by commas, spaces, or line breaks.&#10;Examples:&#10;60401, 60402, 60403&#10;or paste a list:&#10;60401&#10;60402&#10;60403"
                    value={formData.serviceAreas}
                    onChange={handleChange}
                    disabled={loading}
                    helperText={`${formData.serviceAreas ? formData.serviceAreas.split(/[\s,\n]+/).filter(z => z.trim()).length : 0} zip codes entered. You can paste multiple zip codes at once.`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SalesTaxRatesEditor
                    rates={salesTaxRates}
                    onChange={setSalesTaxRates}
                    disabled={loading}
                    coverageAttested={salesTaxCoverageAttested}
                    onCoverageAttestedChange={setSalesTaxCoverageAttested}
                  />
                </Grid>
                {/* Services Selection */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Services You Offer
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select all services you can provide. You will only see service requests for the services you select.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="services.lawnMowingTrimming"
                        checked={formData.services.lawnMowingTrimming}
                        onChange={(e) => {
                          const serviceName = e.target.name.split('.')[1];
                          setFormData(prev => ({
                            ...prev,
                            services: {
                              ...prev.services,
                              [serviceName]: e.target.checked,
                            },
                          }));
                        }}
                        disabled={loading}
                      />
                    }
                    label="Lawn Mowing/Trimming"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="services.weedControlFertilizing"
                        checked={formData.services.weedControlFertilizing}
                        onChange={(e) => {
                          const serviceName = e.target.name.split('.')[1];
                          setFormData(prev => ({
                            ...prev,
                            services: {
                              ...prev.services,
                              [serviceName]: e.target.checked,
                            },
                          }));
                        }}
                        disabled={loading}
                      />
                    }
                    label="Weed Control/Fertilizing"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="services.snowRemoval"
                        checked={formData.services.snowRemoval}
                        onChange={(e) => {
                          const serviceName = e.target.name.split('.')[1];
                          setFormData(prev => ({
                            ...prev,
                            services: {
                              ...prev.services,
                              [serviceName]: e.target.checked,
                            },
                          }));
                        }}
                        disabled={loading}
                      />
                    }
                    label="Snow Removal"
                  />
                </Grid>

                {/* Pesticide License — optional at signup; required later for marketplace weed/fert bids */}
                {formData.services.weedControlFertilizing && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        IDOA Commercial Pesticide Applicator License (Optional now)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Required only if you want to bid on marketplace Weed Control/Fertilizing requests.
                        You can upload later in Settings. You remain responsible for complying with all pesticide laws for your own customers.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <input
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          id="pesticide-license-upload"
                          type="file"
                          onChange={handlePesticideLicenseUpload}
                          disabled={pesticideLicenseUploading || loading}
                        />
                        <label htmlFor="pesticide-license-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            color={pesticideLicenseDocument ? 'success' : 'primary'}
                            startIcon={pesticideLicenseUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                            disabled={pesticideLicenseUploading}
                            fullWidth
                          >
                            {pesticideLicenseUploading ? 'Uploading...' : pesticideLicenseDocument ? 'Pesticide License Uploaded ✓' : 'Upload Pesticide License (Optional)'}
                          </Button>
                        </label>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                          Accepted: Images (JPG, PNG) or PDF, max 10MB
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="pesticideLicenseNumber"
                        label="License Number"
                        name="pesticideLicenseNumber"
                        placeholder="Enter your license number"
                        value={pesticideLicenseDetails.pesticideLicenseNumber}
                        onChange={handlePesticideLicenseDetailsChange}
                        disabled={loading}
                        helperText="Your IDOA Commercial Pesticide Applicator License number"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="pesticideLicenseExpiryDate"
                        label="Expiry Date (Optional)"
                        name="pesticideLicenseExpiryDate"
                        type="date"
                        value={pesticideLicenseDetails.pesticideLicenseExpiryDate}
                        onChange={handlePesticideLicenseDetailsChange}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                        helperText="When your license expires (if applicable)"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {formData.userType === 'provider' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Business Logo (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload your business logo to make your profile more professional.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={businessLogo}
                      sx={{ width: 80, height: 80 }}
                    >
                      <PhotoCamera />
                    </Avatar>
                    
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="logo-upload-register"
                        type="file"
                        onChange={handleLogoUpload}
                        disabled={logoUploading}
                      />
                      <label htmlFor="logo-upload-register">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={logoUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                          disabled={logoUploading}
                        >
                          {logoUploading ? 'Uploading...' : 'Upload Logo'}
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Recommended: Square image, max 5MB
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Marketplace credentials — optional for tools-only; required before marketplace bidding */}
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Business tools vs. marketplace
                    </Typography>
                    <Typography variant="body2" component="span">
                      You can use CRM, scheduling, routes, crew tools, Additional Services, renewals, and the Financial Dashboard
                      without uploading insurance or a pesticide license. To <strong>bid on new marketplace customers</strong>, you must
                      submit proof of liability insurance and be verified by LawnHub (typically 1–2 business days).
                      Weed Control/Fertilizing marketplace bids also require a verified IDOA pesticide applicator license.
                      You remain responsible for carrying any insurance or licenses required by law for your own customers.
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined">
                    Have your own customer service agreement? After you sign up, upload it under{' '}
                    <strong>Customer Agreements</strong> and customers will e-sign it when they accept your bid or
                    onboarding offer. You can download every signed copy.
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Proof of Insurance (Optional — required for marketplace)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload valid liability insurance to unlock marketplace bidding after admin verification.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*,application/pdf"
                      style={{ display: 'none' }}
                      id="insurance-upload-register"
                      type="file"
                      onChange={handleInsuranceUpload}
                      disabled={insuranceUploading}
                    />
                    <label htmlFor="insurance-upload-register">
                      <Button
                        variant="outlined"
                        component="span"
                        color={insuranceProof ? 'success' : 'primary'}
                        startIcon={insuranceUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                        disabled={insuranceUploading}
                        fullWidth
                      >
                        {insuranceUploading ? 'Uploading...' : insuranceProof ? 'Insurance Document Uploaded ✓' : 'Upload Insurance Document (Optional)'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Accepted: Images (JPG, PNG) or PDF, max 10MB
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="insuranceProvider"
                    label="Insurance Company"
                    name="insuranceProvider"
                    placeholder="e.g., State Farm"
                    value={insuranceDetails.insuranceProvider}
                    onChange={handleInsuranceDetailsChange}
                    disabled={loading}
                    helperText="Name of your insurance provider"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="insurancePolicyNumber"
                    label="Policy Number"
                    name="insurancePolicyNumber"
                    placeholder="Policy #"
                    value={insuranceDetails.insurancePolicyNumber}
                    onChange={handleInsuranceDetailsChange}
                    disabled={loading}
                    helperText="Your insurance policy number"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="insuranceExpiryDate"
                    label="Expiry Date"
                    name="insuranceExpiryDate"
                    type="date"
                    value={insuranceDetails.insuranceExpiryDate}
                    onChange={handleInsuranceDetailsChange}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    helperText="When your policy expires"
                  />
                </Grid>

                {/* W-9 Tax Form Section - REQUIRED for Providers */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="error">
                    W-9 Tax Form (Required)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      IRS requires a quick W-9 before we can pay you — takes 30 seconds.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Download a blank W-9 form from{' '}
                      <Link href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer">
                        IRS.gov
                      </Link>
                      {' '}, fill it out, sign it, and upload it here.
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="application/pdf"
                      style={{ display: 'none' }}
                      id="w9-upload"
                      type="file"
                      onChange={handleW9Upload}
                      disabled={w9Uploading || loading}
                    />
                    <label htmlFor="w9-upload">
                      <Button
                        variant="contained"
                        component="span"
                        color={w9Document ? 'success' : 'error'}
                        startIcon={w9Uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                        disabled={w9Uploading}
                        fullWidth
                      >
                        {w9Uploading ? 'Uploading...' : w9Document ? 'W-9 Form Uploaded ✓' : 'Upload W-9 Form (Required)'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      PDF only, max 10MB. Must be signed.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="w9TaxId"
                    label="Tax ID (EIN or SSN)"
                    name="taxId"
                    placeholder="XX-XXXXXXX"
                    value={w9Details.taxId}
                    onChange={handleW9DetailsChange}
                    disabled={loading}
                    helperText="Enter your EIN (business) or SSN (individual)"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Business Type</InputLabel>
                    <Select
                      name="businessType"
                      value={w9Details.businessType}
                      label="Business Type"
                      onChange={(e) => setW9Details({ ...w9Details, businessType: e.target.value as any })}
                      disabled={loading}
                    >
                      <MenuItem value="individual">Individual/Sole Proprietor</MenuItem>
                      <MenuItem value="llc">LLC</MenuItem>
                      <MenuItem value="corporation">Corporation (C-Corp or S-Corp)</MenuItem>
                      <MenuItem value="partnership">Partnership</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="w9BusinessName"
                    label="Full Business Name (for tax/W9 purposes)"
                    name="businessName"
                    placeholder="Enter full legal business name if different from abbreviated name above"
                    value={w9Details.businessName}
                    onChange={handleW9DetailsChange}
                    disabled={loading}
                    helperText="Enter your complete legal business name as it appears on tax documents. Leave blank if it's the same as the abbreviated name entered above."
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {formData.userType === 'provider' ? 'Business Address Information' : 'Address Information'}
              </Typography>
              {formData.userType === 'provider' && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your business address will be used to verify legitimacy and provide location-based services
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <AddressAutocomplete
                value={formData.street}
                onChange={handleAddressSelect}
                label="Street Address"
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                id="city"
                label="City"
                name="city"
                autoComplete="address-level2"
                value={formData.city}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel id="register-state-label">State</InputLabel>
                <Select
                  labelId="register-state-label"
                  id="register-state"
                  name="state"
                  value={formData.state}
                  label="State"
                  onChange={handleSelectChange}
                  disabled={loading}
                >
                  {US_STATES.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                id="zipCode"
                label="ZIP Code"
                name="zipCode"
                autoComplete="postal-code"
                value={formData.zipCode}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            {formData.userType === 'provider' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={agreedToProviderTerms}
                      onChange={(e) => setAgreedToProviderTerms(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      I agree to the{' '}
                      <Link 
                        component="button" 
                        type="button" 
                        onClick={() => handleViewAgreement('provider')}
                        sx={{ textDecoration: 'underline' }}
                      >
                        Provider Service Agreement
                      </Link>
                      {' '}and{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener" sx={{ textDecoration: 'underline' }}>
                        Privacy Policy
                      </Link>
                      {' '}*
                    </Typography>
                  }
                />
              </Grid>
            )}

            {formData.userType === 'customer' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={agreedToCustomerTerms}
                      onChange={(e) => setAgreedToCustomerTerms(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      I agree to the{' '}
                      <Link 
                        component="button" 
                        type="button" 
                        onClick={() => handleViewAgreement('customer')}
                        sx={{ textDecoration: 'underline' }}
                      >
                        Customer Service Agreement
                      </Link>
                      {' '}and{' '}
                      <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener" sx={{ textDecoration: 'underline' }}>
                        Privacy Policy
                      </Link>
                      {' '}*
                    </Typography>
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={agreedToStripeTerms}
                    onChange={(e) => setAgreedToStripeTerms(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" component="span">
                    I agree to the{' '}
                    <Link 
                      href="https://stripe.com/legal/connect-account"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'underline' }}
                    >
                      Stripe payment processing terms and conditions
                    </Link>
                    {' '}*
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={agreedToSMSMessaging}
                    onChange={(e) => setAgreedToSMSMessaging(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" component="span">
                    I agree to receive SMS/text messages regarding my account and services. Message and data rates may apply. Reply STOP to opt out at any time. *
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              * You must agree to all terms indicated with an asterisk (*) to create an account.
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Free Account'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Agreement Dialog */}
      <Dialog
        open={agreementDialog.open}
        onClose={handleCloseAgreement}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {agreementDialog.type === 'provider' ? 'Provider' : 'Customer'} Service Agreement
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please read the following agreement carefully:
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.300',
              maxHeight: 400,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {agreementDialog.type === 'provider' 
              ? (providerAgreement || 'Loading agreement...') 
              : (customerAgreement || 'Loading agreement...')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAgreement}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegisterPage;
